<template>
    <div class="w-full max-w-xl m-auto tex-center">
        <div v-if="Object.keys(errors).length"
            class="flex items-center justify-between mb-8 max-w-3xl border border-red-500">
            <div class="flex items-center">
                <svg class="flex-shrink-0 ml-4 mr-2 w-4 h-4 fill-red-500" xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20">
                    <path
                        d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm1.41-1.41A8 8 0 1 0 15.66 4.34 8 8 0 0 0 4.34 15.66zm9.9-8.49L11.41 10l2.83 2.83-1.41 1.41L10 11.41l-2.83 2.83-1.41-1.41L8.59 10 5.76 7.17l1.41-1.41L10 8.59l2.83-2.83 1.41 1.41z" />
                </svg>
                <ul>
                    <li v-for="(v, k) in errors" :key="k">
                        <div class="py-4 text-red-500 text-sm font-medium">{{ v }}</div>
                    </li>
                </ul>
            </div>
        </div>

        <p class="font-bold mt-10 mb-6 text-lg">Summary of Run Design</p>

        <table class="w-full">
            <tbody class="text-left">
                <tr class="border-b">
                    <th class="py-2">System</th>
                    <td>{{ run.system }}</td>
                </tr>
                <tr class="border-b">
                    <th class="py-2">Drainage Width</th>
                    <td>{{ run.width }}</td>
                </tr>
                <tr class="border-b">
                    <th class="py-2">Drainage Edge</th>
                    <td>{{ run.edge }}</td>
                </tr>

                <tr v-if="run.grate">
                    <th class="text-lg">Grate</th>
                </tr>
                <tr v-if="run.grate" class="border-b">
                    <th class="py-2">Load Class</th>
                    <td class="flex items-end">
                        <class-icon :name="run.grate.icon" class="mx-2 w-6 h-6 fill-blue-800" />
                        {{ run.grate.class }}
                    </td>
                </tr>
                <tr v-if="run.grate" class="border-b">
                    <th class="py-2">Material</th>
                    <td>{{ run.grate.material }}</td>
                </tr>
                <tr v-if="run.grate" class="border-b">
                    <th class="py-2">Compliance</th>
                    <td class="flex pt-2">
                        <span v-for="(icon, j) in run.grate.compliances" :key="j"
                            class="rounded bg-blue-800 w-4 h-4 mx-1">
                            <compliance-icon :name="icon" class="flex-shrink-0 w-4 h-4 p-0.5 fill-white" />
                        </span>
                    </td>
                </tr>

                <tr class="border-b">
                    <th class="py-2">Total Run Length</th>
                    <td>{{ run.length }}</td>
                </tr>
                <tr v-if="run.channel_depth" class="border-b">
                    <th class="py-2">Overall Channel Depth</th>
                    <td>{{ run.channel_depth }}</td>
                </tr>
                <tr class="border-b">
                    <th class="py-2">Constant Depth or Sloped Run</th>
                    <td>{{ run.depth }}</td>
                </tr>
                <tr class="border-b">
                    <th class="py-2">Outlet Configuration</th>
                    <td>{{ run.outlet.name }}</td>
                </tr>
                <tr class="border-b">
                    <th class="py-2">Outlet Type</th>
                    <td>{{ run.outlet.type }}</td>
                </tr>
                <tr v-if="run.outlet_size" class="border-b">
                    <th class="py-2">Outlet Size</th>
                    <td>{{ run.outlet.size }}</td>
                </tr>

                <!-- Selected Accessories Section -->
                <tr v-if="selectedAccessories.length > 0">
                    <th class="text-lg py-4">Selected Accessories</th>
                </tr>
                <tr v-for="(accessory, i) in selectedAccessories" :key="'acc-row-' + i" class="border-b">
                    <th class="py-2">{{ accessory.part_number }}</th>
                    <td>{{ accessory.quantity }} {{ accessory.description ? '- ' + accessory.description : '' }}</td>
                </tr>
            </tbody>
        </table>

        <div class="grid grid-cols-2 sm:grid-cols-3 mt-10 gap-4">
            <a v-for="(item, i) in run.filters" :key="'filter-' + i"
                class="text-center border-solid border-2 p-3 mx-2 border-blue-600 text-blue-600 shadow-lg">
                <img class="m-auto" :src="item.img" />
                <div class="my-4">
                    <p class="font-bold text-gray-600">{{ item.label }}</p>
                    <div class="flex items-center justify-center my-2">
                        <span v-for="(icon, j) in item.compliances" :key="j" class="rounded bg-blue-800 w-4 h-4 mx-1">
                            <compliance-icon :name="icon" class="flex-shrink-0 w-4 h-4 p-0.5 fill-white" />
                        </span>
                    </div>
                    <ul v-for="(part, j) in item.parts" :key="'p-' + j" class="text-left">
                        <li class="text-gray-400 text-sm">{{ part }}</li>
                    </ul>
                </div>
            </a>

            <!-- Accessory Cards -->
            <a v-for="(acc, i) in selectedAccessories" :key="'acc-card-' + i"
                class="text-center border-solid border-2 p-3 mx-2 border-blue-600 text-blue-600 shadow-lg">
                <img v-if="acc.img_path" class="m-auto h-24 object-contain" :src="getImageUrl(acc.img_path)" />
                <div v-else class="m-auto h-24 flex items-center justify-center bg-gray-100">
                    <span class="text-gray-400 text-xs">No image</span>
                </div>
                <div class="my-4">
                    <p class="font-bold text-gray-600">{{ acc.description || acc.part_number }}</p>
                    <p class="text-gray-400 text-sm">{{ acc.part_number }}</p>
                    <p class="text-gray-600 text-sm">{{ acc.quantity }}</p>
                </div>
            </a>
        </div>

        <div class="flex items-center justify-between px-8 py-4">
            <a class="btn-blue bg-gray-600" @click="update('step', value.step - 1)">Previous</a>
            <loading-button :loading="value.processing" class="btn-blue" type="submit">Confirm</loading-button>
        </div>
    </div>
</template>

<script>
import ClassIcon from '@/Shared/LoadClassesIcon.vue'
import ComplianceIcon from '@/Shared/CompliancesIcon.vue'
import LoadingButton from '@/Shared/LoadingButton.vue'

export default {
    components: {
        LoadingButton,
        ClassIcon,
        ComplianceIcon
    },
    props: {
        value: Object,
        run: Object,
        errors: Object
    },
    emits: ['input'],
    computed: {
        selectedAccessories() {
            return this.value.selectedAccessories || []
        }
    },
    methods: {
        update(key, value) {
            this.$emit('input', { key, value })
        },
        // Format image URL properly
        getImageUrl(imgPath) {
            if (!imgPath) return null;

            // Check if it's already a full URL
            if (imgPath.startsWith('http://') || imgPath.startsWith('https://')) {
                return imgPath;
            }

            // Add the appropriate prefix and query parameters
            if (imgPath.startsWith('/')) {
                // If path starts with a slash, don't add another one
                return `${window.location.origin}${imgPath}?w=120&fit=contain`;
            } else {
                // If it's just a relative path like "accessories/...", add /img/ prefix
                return `${window.location.origin}/img/${imgPath}?w=120&fit=contain`;
            }
        }
    }
}
</script>
